/**
 * Partition / equivalence-class math utilities — pure, DOM-free, no Math.random().
 *
 * Reuses relation predicates from `./relation` rather than re-implementing them.
 */

import { isReflexive, isSymmetric, isTransitive, violatingPairs } from './relation';
import type { Pair } from './relation';

export type { Pair };

// ---------------------------------------------------------------------------
// EquivalenceResult
// ---------------------------------------------------------------------------

export interface EquivalenceResult {
  ok: boolean;
  /** Present when ok — the partition blocks, sorted canonically. */
  blocks?: string[][];
  /** Present when !ok — the first failing property. */
  reason?: 'reflexive' | 'symmetric' | 'transitive';
  /** Present when !ok — the violating pairs for `reason`. */
  witness?: Pair[];
}

// ---------------------------------------------------------------------------
// partitionToEquivalence
// ---------------------------------------------------------------------------

/**
 * Given a partition (array of blocks), produce ALL within-block ordered pairs
 * including the reflexive (x,x) pairs.
 *
 * Example: `[['a','b'],['c']]` → `(a,a),(a,b),(b,a),(b,b),(c,c)`.
 */
export function partitionToEquivalence(blocks: readonly (readonly string[])[]): Pair[] {
  const result: Pair[] = [];
  for (const block of blocks) {
    for (const a of block) {
      for (const b of block) {
        result.push([a, b]);
      }
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// isEquivalence
// ---------------------------------------------------------------------------

/**
 * Returns true iff `r` is reflexive on `on`, symmetric, AND transitive.
 */
export function isEquivalence(r: readonly Pair[], on: readonly string[]): boolean {
  return isReflexive(r, on) && isSymmetric(r) && isTransitive(r);
}

// ---------------------------------------------------------------------------
// Internal: group elements by equivalence class (Union-Find / BFS)
// ---------------------------------------------------------------------------

/**
 * Groups `on` into equivalence classes induced by `r`.
 * Elements are assigned to the class of the first element they are related to
 * (in `on` order). Elements within each block are sorted; blocks are sorted by
 * their first element — both alphabetically — so the canonical form is stable.
 *
 * Assumes `r` is a valid equivalence on `on`.
 */
function groupByClass(r: readonly Pair[], on: readonly string[]): string[][] {
  // Build adjacency: for each element, collect all elements it is related to.
  const relatedTo = new Map<string, Set<string>>();
  for (const x of on) {
    relatedTo.set(x, new Set<string>());
  }
  for (const [a, b] of r) {
    relatedTo.get(a)?.add(b);
    relatedTo.get(b)?.add(a);
  }

  // BFS to find connected components (equivalence classes).
  const visited = new Set<string>();
  const blocks: string[][] = [];

  for (const x of on) {
    if (visited.has(x)) {
      continue;
    }
    // BFS from x
    const block: string[] = [];
    const queue: string[] = [x];
    visited.add(x);
    while (queue.length > 0) {
      const curr = queue.shift()!;
      block.push(curr);
      for (const neighbour of relatedTo.get(curr) ?? []) {
        if (!visited.has(neighbour)) {
          visited.add(neighbour);
          queue.push(neighbour);
        }
      }
    }
    // Sort elements within the block for canonical ordering.
    block.sort();
    blocks.push(block);
  }

  // Sort blocks by their first element for canonical ordering.
  blocks.sort((a, b) => (a[0] ?? '').localeCompare(b[0] ?? ''));

  return blocks;
}

// ---------------------------------------------------------------------------
// equivalenceToPartition
// ---------------------------------------------------------------------------

/**
 * Checks whether `r` is an equivalence relation on `on`.
 *
 * - If it fails, returns `{ ok: false, reason, witness }` where `reason` is the
 *   first failing property (checked in order: reflexive → symmetric → transitive)
 *   and `witness` is the violating pairs for that property.
 * - If it passes, groups `on` into equivalence-class blocks and returns
 *   `{ ok: true, blocks }` with blocks and elements in canonical sorted order.
 *
 * Round-trip guarantee:
 *   `equivalenceToPartition(partitionToEquivalence(P), elements)` returns
 *   `{ ok: true, blocks: P_sorted }` where P_sorted has elements within each block
 *   and blocks themselves sorted alphabetically.
 */
export function equivalenceToPartition(
  r: readonly Pair[],
  on: readonly string[],
): EquivalenceResult {
  if (!isReflexive(r, on)) {
    return { ok: false, reason: 'reflexive', witness: violatingPairs('reflexive', r, on) };
  }
  if (!isSymmetric(r)) {
    return { ok: false, reason: 'symmetric', witness: violatingPairs('symmetric', r, on) };
  }
  if (!isTransitive(r)) {
    return { ok: false, reason: 'transitive', witness: violatingPairs('transitive', r, on) };
  }

  const blocks = groupByClass(r, on);
  return { ok: true, blocks };
}

// ---------------------------------------------------------------------------
// quotient
// ---------------------------------------------------------------------------

/**
 * Returns the equivalence-class blocks of `on` under `r` (the quotient set A/∼).
 *
 * Assumes `r` is a valid equivalence on `on`. Blocks and elements within blocks
 * are in canonical sorted order (same as `equivalenceToPartition`'s `blocks`).
 */
export function quotient(on: readonly string[], r: readonly Pair[]): string[][] {
  return groupByClass(r, on);
}

// ---------------------------------------------------------------------------
// bellNumber — via Bell triangle
// ---------------------------------------------------------------------------

/**
 * Returns the nth Bell number B(n) using the Bell triangle.
 *
 * Bell triangle construction:
 *   Row 0: [1]
 *   Each subsequent row starts with the last element of the previous row,
 *   then each next element is the sum of the element to its left and the
 *   element directly above that left element.
 *
 * B(n) = first element of row n (= last element of row n-1 for n > 0).
 *
 * Known values: B(0)=1, B(1)=1, B(2)=2, B(3)=5, B(4)=15, B(5)=52, B(6)=203.
 */
export function bellNumber(n: number): number {
  if (n === 0) {
    return 1;
  }

  // prev holds the previous row of the Bell triangle.
  let prev: number[] = [1];

  for (let i = 1; i <= n; i++) {
    // Each new row starts with the last element of the previous row.
    const first = prev[prev.length - 1] ?? 1;
    const row: number[] = [first];
    for (let j = 1; j <= i; j++) {
      // row[j] = row[j-1] + prev[j-1]
      const left = row[j - 1] ?? 0;
      const above = prev[j - 1] ?? 0;
      row.push(left + above);
    }
    prev = row;
  }

  return prev[0] ?? 1;
}
